import React, { useState, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { useClickOutside } from '../hooks/useClickOutside';

/**
 * Single-select dropdown that opens a themed list-group panel (same look as SearchableDropdown).
 * Use instead of Form.Select when you want the options popup to match the site theme.
 * @param {string} name - For onChange event target.name
 * @param {string} value - Current value (must match an option value)
 * @param {Array<{ value: string, label: string }>} options
 * @param {function} onChange - (e) => void, e.target = { name, value }
 * @param {string} [id] - For aria-controls / id on panel
 * @param {boolean} [disabled]
 */
const ThemedSelect = ({ name, value, options = [], onChange, id, disabled }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useClickOutside(containerRef, () => setOpen(false));

  const selectedOption = options.find((opt) => opt.value === value);
  const displayLabel = selectedOption ? selectedOption.label : '';

  const handleSelect = (opt) => {
    onChange({ target: { name, value: opt.value } });
    setOpen(false);
  };

  const safeId = id || `themed-select-${name}`;

  return (
    <div ref={containerRef} className="position-relative">
      <Form.Control
        type="text"
        name={name}
        value={displayLabel}
        readOnly
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        aria-expanded={open}
        aria-controls={safeId}
        aria-haspopup="listbox"
        role="combobox"
        aria-autocomplete="none"
        style={{ cursor: disabled ? undefined : 'pointer', caretColor: 'transparent' }}
      />
      {open && (
        <ul
          className="list-group position-absolute w-100 mt-1"
          style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 10 }}
          role="listbox"
          id={safeId}
        >
          {options.length === 0 ? (
            <li className="list-group-item list-group-item-secondary" role="option">
              No options
            </li>
          ) : (
            options.map((opt) => (
              <li
                key={opt.value ?? 'empty'}
                role="option"
                tabIndex={0}
                aria-selected={opt.value === value}
                className="list-group-item list-group-item-action"
                onClick={() => handleSelect(opt)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(opt);
                  }
                }}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default ThemedSelect;
